import { useEffect, useRef, useState } from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Button,
  makeStyles,
} from '@fluentui/react-components';
import { useNavigate } from 'react-router-dom';
import { useUsageStore } from '../stores/usageStore.js';
import { UsageMeter } from './UsageMeter.js';
import { TiendaDialog } from './TiendaDialog.js';
import { formatBytes } from '../api/storage.js';
import { tokensPct, monthRemaining } from '../api/usage.js';
import {
  approxFromRemaining,
  fallbackPlanEquivalence,
} from '../lib/planEquivalence.js';
import { NOTA_EQUIVALENCIA, enCreditos, etiquetaCreditos, formatCreditos } from '../lib/creditos.js';
import { controlDelHeader } from './controlDelHeader.js';

const fmtN = new Intl.NumberFormat('es-MX');
const fmtCompact = new Intl.NumberFormat('es-MX', {
  notation: 'compact',
  maximumFractionDigits: 1,
});
const fmtRenewDate = new Intl.DateTimeFormat('es-MX', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
});
const fmtDayMonth = new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'short' });
const fmtDay = new Intl.DateTimeFormat('es-MX', { day: 'numeric' });

const useStyles = makeStyles({
  /** «OPUS Pro · 19%»: mismo control del header que el chip de créditos (rectángulo, borde fino). */
  pill: {
    ...controlDelHeader,
    gap: '8px',
    fontWeight: 400,
  },
  ring: { width: '18px', height: '18px', flexShrink: 0 },
  text: {
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: '4px',
    maxWidth: '280px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  planLabel: { color: 'var(--text-secondary)' },
  planName: { color: 'var(--text-primary)', fontWeight: 700 },
  pctValue: { color: 'var(--text-primary)', fontWeight: 700 },
  popover: {
    minWidth: '360px',
    maxWidth: '420px',
    maxHeight: 'min(75vh, 620px)',
    overflowY: 'auto',
    padding: 'var(--space-l) var(--space-xl)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-s)',
  },
  header: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 'var(--space-m)',
  },
  headerTitle: {
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    fontSize: 'var(--fs-400)',
  },
  headerPeriod: {
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--fs-200)',
    color: 'var(--text-tertiary)',
    whiteSpace: 'nowrap',
  },
  renewNote: {
    margin: 0,
    fontSize: 'var(--fs-100)',
    color: 'var(--text-tertiary)',
    lineHeight: 1.4,
  },
  warnBanner: {
    margin: 0,
    padding: '8px 10px',
    borderRadius: '8px',
    fontSize: 'var(--fs-200)',
    lineHeight: 1.4,
  },
  warn80: {
    background: 'var(--warn-weak, rgba(245, 158, 11, 0.12))',
    border: '1px solid var(--warn)',
    color: 'var(--text-primary)',
  },
  warn95: {
    background: 'var(--bad-weak, rgba(239, 68, 68, 0.12))',
    border: '1px solid var(--bad)',
    color: 'var(--text-primary)',
  },
  tokenBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: '10px 0 4px',
  },
  tokenNumbers: {
    fontSize: 'var(--fs-300)',
    color: 'var(--text-secondary)',
    lineHeight: 1.35,
  },
  tokenTrack: {
    height: '10px',
    borderRadius: '999px',
    background: 'var(--bg-overlay)',
    overflow: 'hidden',
  },
  tokenFill: {
    height: '100%',
    borderRadius: '999px',
    transition: 'width 200ms ease',
  },
  eqLine: {
    margin: 0,
    fontSize: 'var(--fs-200)',
    color: 'var(--text-secondary)',
    lineHeight: 1.4,
  },
  sectionLabel: {
    marginTop: 'var(--space-s)',
    fontSize: 'var(--fs-100)',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--text-tertiary)',
  },
  packRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '8px',
    fontSize: 'var(--fs-200)',
    color: 'var(--text-secondary)',
  },
  packVal: { fontWeight: 600, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' },
  activityRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '8px',
    fontSize: 'var(--fs-200)',
    color: 'var(--text-secondary)',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--space-m)',
    marginTop: 'var(--space-s)',
  },
  detailLink: {
    fontSize: 'var(--fs-200)',
    color: 'var(--text-secondary)',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    ':hover': { color: 'var(--text-primary)', textDecoration: 'underline' },
  },
});

function planDisplayName(usage: NonNullable<ReturnType<typeof useUsageStore.getState>['data']>): string {
  if (usage.plan_display?.trim()) return usage.plan_display.trim();
  const m: Record<string, string> = { start: 'Start', pro: 'Pro', teams: 'Teams', vitruvio: 'Vitruvio' };
  return m[usage.plan] ?? usage.plan;
}

function parseDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function monthRangeLabel(start: Date, end: Date): string {
  // end es exclusivo (00:00 del mes siguiente); mostrar último día del mes.
  const lastDay = new Date(end.getTime() - 1);
  if (start.getMonth() === lastDay.getMonth() && start.getFullYear() === lastDay.getFullYear()) {
    return `${fmtDay.format(start)}–${fmtDayMonth.format(lastDay)}`;
  }
  return `${fmtDayMonth.format(start)}–${fmtDayMonth.format(lastDay)}`;
}

export function UsagePill() {
  const styles = useStyles();
  const navigate = useNavigate();
  const usage = useUsageStore((s) => s.data);
  const [storeOpen, setStoreOpen] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const surfaceRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!popoverOpen) return;
    const raf = requestAnimationFrame(() => {
      const el = surfaceRef.current;
      if (!el) return;
      el.focus({ preventScroll: true });
      el.scrollTop = 0;
    });
    return () => cancelAnimationFrame(raf);
  }, [popoverOpen]);

  if (!usage) return null;

  const displayName = planDisplayName(usage);
  const cr = enCreditos(usage);
  const { used, quota } = usage.tokens;
  const remMonth = monthRemaining(usage);
  const packTokens = usage.tokens.pack_tokens ?? 0;
  const pct = tokensPct(usage);
  const rawPct = quota > 0 ? (used / quota) * 100 : 0;
  const ringColor =
    rawPct >= 90 ? 'var(--bad)' :
    rawPct >= 75 ? 'var(--warn)' :
    'var(--accent)';
  const circ = 2 * Math.PI * 15;
  const dashOffset = circ * (1 - pct / 100);

  const monthStart = parseDate(usage.tokens.month_start);
  const monthEnd = parseDate(usage.tokens.month_end);
  const eqRaw = usage.plan_equivalence
    ?? fallbackPlanEquivalence(usage.plan, usage.seats ?? 1);
  const eqTokens = eqRaw.tokens_month || eqRaw.tokens_week || quota || 1;
  const approx = approxFromRemaining(remMonth, {
    concepts: eqRaw.concepts,
    apus: eqRaw.apus,
    chats: eqRaw.chats,
    tokens_month: eqTokens,
  });

  return (
    <>
      <Popover
        withArrow
        positioning="below-end"
        open={popoverOpen}
        onOpenChange={(_, d) => setPopoverOpen(d.open)}
      >
        <PopoverTrigger disableButtonEnhancement>
          <button
            type="button"
            className={styles.pill}
            aria-label={`${displayName} · ${pct}% de ${cr ? 'créditos' : 'tokens'} usados este mes`}
          >
            <svg className={styles.ring} viewBox="0 0 36 36" aria-hidden="true">
              <circle cx="18" cy="18" r="15" fill="none" stroke="var(--bg-overlay)" strokeWidth="4" />
              <circle
                cx="18" cy="18" r="15" fill="none"
                stroke={ringColor} strokeWidth="4" strokeLinecap="round"
                strokeDasharray={circ} strokeDashoffset={dashOffset}
                transform="rotate(-90 18 18)"
              />
            </svg>
            <span className={styles.text}>
              <span className={styles.planName}>{displayName}</span>
              <span className={styles.planLabel}>·</span>
              <span className={styles.pctValue}>{pct}%</span>
            </span>
          </button>
        </PopoverTrigger>
        <PopoverSurface ref={surfaceRef} tabIndex={-1} className={styles.popover}>
          <div className={styles.header}>
            <span className={styles.headerTitle}>{displayName}</span>
            {monthStart && monthEnd ? (
              <span className={styles.headerPeriod}>
                Tu mes · {monthRangeLabel(monthStart, monthEnd)}
              </span>
            ) : (
              <span className={styles.headerPeriod}>Tu mes</span>
            )}
          </div>
          {monthEnd && (
            <p className={styles.renewNote}>
              Se renueva el {fmtRenewDate.format(monthEnd)}
              {usage.plan === 'teams' ? ' · Bolsa compartida del equipo' : ''}
            </p>
          )}

          {rawPct >= 90 && (
            <p className={`${styles.warnBanner} ${styles.warn95}`}>
              Vas al {pct}% de tus {cr ? 'créditos' : 'tokens'} del mes. Compra una recarga para no quedarte corto.
            </p>
          )}
          {rawPct >= 75 && rawPct < 90 && (
            <p className={`${styles.warnBanner} ${styles.warn80}`}>
              Has usado el {pct}% de tus {cr ? 'créditos' : 'tokens'} del mes.
            </p>
          )}

          <div className={styles.tokenBlock}>
            {cr ? (
              <span className={styles.tokenNumbers}>
                <strong style={{ color: 'var(--text-primary)', fontSize: '1.25em' }} title={`${fmtN.format(used)} tokens`}>
                  {formatCreditos(used, 'saldo')}
                </strong>
                {' de '}
                <span title={`${fmtN.format(quota)} tokens`}>{etiquetaCreditos(quota)}</span>
                {' · quedan '}
                <strong style={{ color: 'var(--text-primary)' }} title={`${fmtN.format(remMonth)} tokens`}>
                  {formatCreditos(remMonth, 'saldo')}
                </strong>
              </span>
            ) : (
            <span className={styles.tokenNumbers}>
              <strong style={{ color: 'var(--text-primary)', fontSize: '1.25em' }} title={fmtN.format(used)}>
                {fmtCompact.format(used)}
              </strong>
              {' de '}
              <span title={fmtN.format(quota)}>{fmtCompact.format(quota)}</span>
              {' tokens · quedan '}
              <strong style={{ color: 'var(--text-primary)' }} title={fmtN.format(remMonth)}>
                {fmtCompact.format(remMonth)}
              </strong>
            </span>
            )}
            <div className={styles.tokenTrack}>
              <div
                className={styles.tokenFill}
                style={{
                  width: `${pct}%`,
                  background: rawPct >= 90 ? 'var(--bad)' : rawPct >= 75 ? 'var(--warn)' : 'var(--ok)',
                }}
                role="progressbar"
                aria-valuenow={used}
                aria-valuemin={0}
                aria-valuemax={quota}
              />
            </div>
            <p className={styles.eqLine}>
              ≈ {fmtN.format(approx.concepts)} conceptos · {fmtN.format(approx.apus)} APUs · {fmtN.format(approx.chats)} chats
              {cr ? ' (promedios)' : ''}
            </p>
            {cr && <p className={styles.eqLine}>{NOTA_EQUIVALENCIA}</p>}
          </div>

          <div className={styles.sectionLabel}>Orden de consumo</div>
          <div className={styles.packRow}>
            <span>1. Bolsa mensual</span>
            <span className={styles.packVal}>
              {cr
                ? `${formatCreditos(Math.min(used, quota), 'saldo')} / ${formatCreditos(quota, 'saldo')}`
                : `${fmtCompact.format(Math.min(used, quota))} / ${fmtCompact.format(quota)}`}
            </span>
          </div>
          <div className={styles.packRow}>
            <span>2. Recargas</span>
            <span className={styles.packVal}>
              {cr ? etiquetaCreditos(packTokens) : `${fmtCompact.format(packTokens)} tokens`}
            </span>
          </div>

          <div className={styles.sectionLabel}>Actividad del mes · {usage.period}</div>
          <div className={styles.activityRow}>
            <span>Conceptos cotizados</span>
            <span className={styles.packVal}>{fmtN.format(usage.counters.concepts_quoted)}</span>
          </div>
          <div className={styles.activityRow}>
            <span>APUs con IA</span>
            <span className={styles.packVal}>{fmtN.format(usage.counters.apus_generated)}</span>
          </div>
          <div className={styles.activityRow}>
            <span>Chats</span>
            <span className={styles.packVal}>{fmtN.format(usage.counters.chats_sent)}</span>
          </div>

          <div className={styles.sectionLabel}>Catálogos</div>
          <div className={styles.packRow}>
            <span>Propios activos</span>
            <span className={styles.packVal}>
              {fmtN.format(usage.counters.catalogs_active)}
              {usage.limits.catalogs_active != null && ` / ${fmtN.format(usage.limits.catalogs_active)}`}
            </span>
          </div>
          <p className={styles.renewNote}>eCostos siempre incluido — no cuenta aquí</p>

          {usage.storage && (
            <>
              <div className={styles.sectionLabel}>Almacenamiento</div>
              <UsageMeter
                label={usage.storage.scope === 'tenant' ? 'Espacio del equipo' : 'Espacio personal'}
                current={Math.round(usage.storage.used_bytes / (1024 * 1024))}
                limit={Math.max(1, Math.round(usage.storage.quota_bytes / (1024 * 1024)))}
                unit="MB"
              />
              <p style={{ margin: 0, fontSize: 'var(--fs-100)', color: 'var(--text-tertiary)' }}>
                {formatBytes(usage.storage.used_bytes)} de {formatBytes(usage.storage.quota_bytes)}
              </p>
            </>
          )}

          <div className={styles.footer}>
            <Button
              appearance="primary"
              size="small"
              onClick={() => {
                setPopoverOpen(false);
                setStoreOpen(true);
              }}
            >
              {cr ? 'Comprar créditos' : 'Comprar tokens'}
            </Button>
            <button
              type="button"
              className={styles.detailLink}
              onClick={() => {
                setPopoverOpen(false);
                navigate('/account');
              }}
            >
              Ver detalle de la cuenta →
            </button>
          </div>
        </PopoverSurface>
      </Popover>
      <TiendaDialog open={storeOpen} onClose={() => setStoreOpen(false)} />
    </>
  );
}
